import { useEffect, useRef, useState } from "react"
import { useTranslation, type TranslationKeys } from "@/hooks/useTranslation/useTranslation"

const ANALYSIS_STEPS: { labelKey: TranslationKeys; duration: number }[] = [
  { labelKey: "QMS/ANALYZING/DOC_STRUCTURE", duration: 1800 },
  { labelKey: "QMS/ANALYZING/DETECTING_HEADERS", duration: 1400 },
  { labelKey: "QMS/ANALYZING/LOADING_EXTRACTS", duration: 1600 },
  { labelKey: "QMS/ANALYZING/CLASSIFYING_TYPES", duration: 1100 },
  { labelKey: "QMS/ANALYZING/HIERARCHY", duration: 1500 },
  { labelKey: "QMS/ANALYZING/RELATIONS", duration: 1500 },
  { labelKey: "QMS/ANALYZING/SCHEMA_ASSEMBLY", duration: 1100 },
]

export function AnalyzingProgress({
  feedbackRound,
  dataReady,
  onComplete,
}: {
  feedbackRound: boolean
  dataReady: boolean
  onComplete: () => void
}) {
  const { translate } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState(0)
  const [phase, setPhase] = useState<"running" | "waiting">("running")

  // Parent passes a fresh onComplete arrow every render. Pin it in a ref so
  // the finishing effect's dep array doesn't churn and clear our interval.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  // Guard so the phase-2 interval starts exactly once.
  const finishingStarted = useRef(false)

  // Phase 1: always play the full ~10s irregular simulation up to 95%
  useEffect(() => {
    if (phase !== "running") return
    let cancelled = false
    let elapsed = 0
    const total = ANALYSIS_STEPS.reduce((s, x) => s + x.duration, 0)
    let timer: ReturnType<typeof setTimeout>

    const tick = () => {
      if (cancelled) return
      // Irregular cadence: 70–260ms ticks simulate bursty AI work
      const delta = 70 + Math.random() * 190
      elapsed += delta

      let cum = 0
      let idx = ANALYSIS_STEPS.length - 1
      for (let i = 0; i < ANALYSIS_STEPS.length; i++) {
        cum += ANALYSIS_STEPS[i].duration
        if (elapsed < cum) {
          idx = i
          break
        }
      }
      setCurrentStep(idx)

      const target = Math.min(95, (elapsed / total) * 100)
      const jitter = (Math.random() - 0.4) * 2.5
      setProgress((p) => Math.min(95, Math.max(p, target + jitter)))

      if (elapsed < total) {
        timer = setTimeout(tick, delta)
      } else {
        setProgress(95)
        setCurrentStep(ANALYSIS_STEPS.length - 1)
        setPhase("waiting")
      }
    }

    timer = setTimeout(tick, 120)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [phase])

  // Phase 2: once simulation reached 95% AND the real response arrived,
  // quickly fill to 100% and hand off. Runs exactly once per mount.
  useEffect(() => {
    if (phase !== "waiting" || !dataReady) return
    if (finishingStarted.current) return
    finishingStarted.current = true
    setCurrentStep(ANALYSIS_STEPS.length)
    let p = 95
    const id = setInterval(() => {
      p += 1
      if (p >= 100) {
        clearInterval(id)
        setProgress(100)
        setTimeout(() => onCompleteRef.current(), 250)
      } else {
        setProgress(p)
      }
    }, 30)
    return () => clearInterval(id)
  }, [phase, dataReady])

  const safeIdx = Math.min(currentStep, ANALYSIS_STEPS.length - 1)
  const currentLabel = translate(ANALYSIS_STEPS[safeIdx].labelKey)

  return (
    <div className="space-y-6 py-10">
      <div className="space-y-2 text-center">
        <p className="text-lg font-medium text-white">
          {feedbackRound
            ? translate("QMS/ANALYZING/FEEDBACK_REANALYZE")
            : translate("QMS/ANALYZING/HEADER")}
        </p>
        <p className="text-sm text-grey5">{currentLabel}...</p>
      </div>

      <div className="mx-auto max-w-md space-y-2">
        <div className="h-2 overflow-hidden rounded-full bg-grey4">
          <div
            className="h-full bg-yellow1 transition-[width] duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-grey5">
          <span>
            {translate("QMS/ANALYZING/STEP_OF", {
              current: Math.min(currentStep + 1, ANALYSIS_STEPS.length),
              total: ANALYSIS_STEPS.length,
            })}
          </span>
          <span>{Math.round(progress)}%</span>
        </div>
      </div>

      <ul className="mx-auto max-w-md space-y-1.5">
        {ANALYSIS_STEPS.map((s, i) => {
          const isDone = i < currentStep
          const isActive = i === currentStep
          return (
            <li
              key={s.labelKey}
              className={`flex items-center gap-2 text-xs transition-colors ${
                isDone ? "text-grey5" : isActive ? "text-white" : "text-grey4"
              }`}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {isDone ? (
                  <svg
                    className="h-3 w-3 text-success"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M2 6l3 3 5-6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : isActive ? (
                  <span className="h-2 w-2 animate-pulse rounded-full bg-yellow1" />
                ) : (
                  <span className="h-2 w-2 rounded-full border border-grey4" />
                )}
              </span>
              <span>{translate(s.labelKey)}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
